import { clamp, lerp, saturate, signedWrappedDelta, wrap01 } from "../physics/numbers";
import { sampleAuthoredTrackCurvature } from "../track/definition";
import { createVehicleDynamics } from "./config";
import { createNeutralInput } from "./input";
import type { GameConfig, InputFrame, RivalProfile, VehicleState } from "./types";

const TAU = Math.PI * 2;

export const CAPE_BATTLE_SECTOR = {
  approachStart: 0.078,
  start: 0.095,
  end: 0.18,
  releaseEnd: 0.202,
  rivalryApproachStart: 0.175,
  rivalryStart: 0.215,
  rivalryEnd: 0.36,
  rivalryReleaseEnd: 0.382
} as const;

type CapeBattleRole = {
  side: -1 | 1;
  openingGap: number;
  closingGap: number;
  rivalryGap?: {
    opening: number;
    closing: number;
  };
};

type AuthoredPhaseKeyframe = {
  progress: number;
  value: number;
};

const MAREA_RIVALRY_GAP = { opening: -5.5, closing: 6 } as const;

const CAPE_BATTLE_ROLES: Readonly<Record<string, CapeBattleRole>> = {
  "rival-1": {
    side: -1,
    openingGap: 11,
    closingGap: -5.5,
    rivalryGap: MAREA_RIVALRY_GAP
  },
  "rival-2": { side: 1, openingGap: 23, closingGap: 14 }
};

const MAREA_RIVALRY_PHASES = {
  gap: [
    { progress: 0, value: MAREA_RIVALRY_GAP.opening },
    { progress: 0.22, value: 3.2 },
    { progress: 0.42, value: 1.4 },
    { progress: 0.58, value: -7.2 },
    { progress: 1, value: MAREA_RIVALRY_GAP.closing }
  ],
  laneDistance: [
    { progress: 0, value: 3.4 },
    { progress: 0.22, value: 4.05 },
    { progress: 0.3, value: 4.8 },
    { progress: 0.42, value: 4.1 },
    { progress: 0.58, value: 4.75 },
    { progress: 1, value: 4 }
  ],
  pace: [
    { progress: 0, value: 2 },
    { progress: 0.12, value: 6 },
    { progress: 0.22, value: 3 },
    { progress: 0.42, value: 0 },
    { progress: 0.5, value: -7 },
    { progress: 0.58, value: -3 },
    { progress: 0.7, value: 6 },
    { progress: 1, value: 3 }
  ]
} as const;

export type CapeBattleProfile = {
  engagement: number;
  progress: number;
  gap: number;
  desiredGap: number;
  laneTarget: number;
  paceCorrection: number;
};

const smoothstep = (value: number, minimum: number, maximum: number): number => {
  const factor = saturate((value - minimum) / (maximum - minimum));
  return factor * factor * (3 - factor * 2);
};

const interpolateAuthoredPhase = (
  progress: number,
  start: number,
  end: number,
  startValue: number,
  endValue: number
): number => lerp(startValue, endValue, smoothstep(progress, start, end));

const sampleAuthoredPhase = (
  progress: number,
  keyframes: readonly AuthoredPhaseKeyframe[]
): number => {
  let previous = keyframes[0];

  if (previous === undefined) {
    return 0;
  }

  for (let index = 1; index < keyframes.length; index += 1) {
    const current = keyframes[index];

    if (current === undefined) {
      continue;
    }

    if (progress <= current.progress) {
      return interpolateAuthoredPhase(
        progress,
        previous.progress,
        current.progress,
        previous.value,
        current.value
      );
    }

    previous = current;
  }

  return previous.value;
};

const getSectorEngagement = (
  player: VehicleState,
  approachStart: number,
  start: number,
  end: number,
  releaseEnd: number,
  minimumSpeed: number,
  readySpeed: number
): number => {
  const approach = smoothstep(
    player.trackProgress,
    approachStart,
    start
  );
  const release = 1 - smoothstep(
    player.trackProgress,
    end,
    releaseEnd
  );
  const speedReadiness = smoothstep(player.speed, minimumSpeed, readySpeed);
  const recoveryRelease = 1 - smoothstep(player.feedback.recovery, 0.08, 0.52);
  return approach * release * speedReadiness * recoveryRelease;
};

const getBattleLaneTarget = (
  playerLateralOffset: number,
  preferredSide: -1 | 1,
  roadHalfWidth: number
): number => {
  const laneLimit = Math.max(1.8, roadHalfWidth - 3.15);
  const preferredLane = clamp(
    playerLateralOffset + preferredSide * 3.4,
    -laneLimit,
    laneLimit
  );
  const alternateLane = clamp(
    playerLateralOffset - preferredSide * 3.4,
    -laneLimit,
    laneLimit
  );
  return Math.abs(preferredLane - playerLateralOffset) >= 2.85
    ? preferredLane
    : alternateLane;
};

export const calculateCapeBattleProfile = (
  vehicle: VehicleState,
  profile: RivalProfile,
  traffic: readonly VehicleState[]
): CapeBattleProfile => {
  const role = CAPE_BATTLE_ROLES[vehicle.id];
  const player = traffic.find((other) => other.id === "player");

  if (role === undefined || player === undefined) {
    return {
      engagement: 0,
      progress: 0,
      gap: Number.POSITIVE_INFINITY,
      desiredGap: 0,
      laneTarget: profile.laneBias,
      paceCorrection: 0
    };
  }

  const openingProgress = smoothstep(
    player.trackProgress,
    CAPE_BATTLE_SECTOR.start,
    CAPE_BATTLE_SECTOR.end
  );
  const rivalryProgress = smoothstep(
    player.trackProgress,
    CAPE_BATTLE_SECTOR.rivalryStart,
    CAPE_BATTLE_SECTOR.rivalryEnd
  );
  const rivalryPhase = saturate(
    (player.trackProgress - CAPE_BATTLE_SECTOR.rivalryStart) /
      (CAPE_BATTLE_SECTOR.rivalryEnd - CAPE_BATTLE_SECTOR.rivalryStart)
  );
  const openingEngagement = getSectorEngagement(
    player,
    CAPE_BATTLE_SECTOR.approachStart,
    CAPE_BATTLE_SECTOR.start,
    CAPE_BATTLE_SECTOR.end,
    CAPE_BATTLE_SECTOR.releaseEnd,
    24,
    46
  );
  const rivalryEngagement = role.rivalryGap === undefined
    ? 0
    : getSectorEngagement(
      player,
      CAPE_BATTLE_SECTOR.rivalryApproachStart,
      CAPE_BATTLE_SECTOR.rivalryStart,
      CAPE_BATTLE_SECTOR.rivalryEnd,
      CAPE_BATTLE_SECTOR.rivalryReleaseEnd,
      14,
      28
    );
  const phaseBlend = rivalryEngagement /
    Math.max(Number.EPSILON, openingEngagement + rivalryEngagement);
  const gap = signedWrappedDelta(player.trackProgress, vehicle.trackProgress) *
    profile.dynamics.trackLength;
  const openingDesiredGap = lerp(role.openingGap, role.closingGap, openingProgress);
  const rivalryDesiredGap = role.rivalryGap === undefined
    ? openingDesiredGap
    : sampleAuthoredPhase(rivalryPhase, MAREA_RIVALRY_PHASES.gap);
  const desiredGap = lerp(openingDesiredGap, rivalryDesiredGap, phaseBlend);
  const distanceReadiness = 1 - smoothstep(Math.abs(gap - desiredGap), 48, 88);
  const engagement = Math.max(openingEngagement, rivalryEngagement) * distanceReadiness;
  const baseLaneTarget = getBattleLaneTarget(
    player.lateralOffset,
    role.side,
    profile.dynamics.roadHalfWidth
  );
  const rivalryLaneDistance = role.rivalryGap === undefined
    ? Math.abs(baseLaneTarget - player.lateralOffset)
    : sampleAuthoredPhase(rivalryPhase, MAREA_RIVALRY_PHASES.laneDistance);
  const laneLimit = Math.max(1.8, profile.dynamics.roadHalfWidth - 3.15);
  const rivalryLaneTarget = role.rivalryGap === undefined
    ? baseLaneTarget
    : clamp(
      player.lateralOffset * 0.72 + role.side * rivalryLaneDistance,
      -laneLimit,
      laneLimit
    );
  const laneTarget = lerp(baseLaneTarget, rivalryLaneTarget, rivalryEngagement);
  const rivalryPace = role.rivalryGap === undefined
    ? 0
    : sampleAuthoredPhase(rivalryPhase, MAREA_RIVALRY_PHASES.pace) * rivalryEngagement;
  const correctionResponse = rivalryEngagement > 0 ? 0.24 : 0.18;
  const correctionMinimum = rivalryEngagement > 0 ? -10 : -4;
  const correctionMaximum = rivalryEngagement > 0 ? 6 : 3;
  const paceCorrection = clamp(
    (desiredGap - gap) * correctionResponse + rivalryPace,
    correctionMinimum,
    correctionMaximum
  );

  return {
    engagement,
    progress: lerp(openingProgress, rivalryProgress, phaseBlend),
    gap,
    desiredGap,
    laneTarget,
    paceCorrection
  };
};

export const sampleTrackCurvature = (trackProgress: number): number => {
  return sampleAuthoredTrackCurvature(trackProgress);
};

export const sampleRacingLine = (
  trackProgress: number,
  laneBias = 0,
  linePhase = 0
): number => {
  const progress = wrap01(trackProgress);
  const curvature = sampleTrackCurvature(progress + 0.014);
  const lineVariation = Math.sin(TAU * (progress * 5 + linePhase)) * 0.28;
  return clamp(-curvature * 1.45 + lineVariation + laneBias, -2.6, 2.6);
};

export const createRivalProfiles = (config: GameConfig): RivalProfile[] => {
  const baseConfig = { trackLength: config.trackLength, roadHalfWidth: config.roadHalfWidth };

  return [
    {
      id: "rival-1",
      name: "Marea",
      laneBias: -0.28,
      linePhase: 0.11,
      aggression: 0.98,
      passSide: -1,
      dynamics: createVehicleDynamics(baseConfig, {
        maximumSpeed: 88.8,
        boostedMaximumSpeed: 99,
        engineAcceleration: 17.9,
        steeringResponse: 4.65
      })
    },
    {
      id: "rival-2",
      name: "Vela",
      laneBias: 0.35,
      linePhase: 0.29,
      aggression: 1.01,
      passSide: 1,
      dynamics: createVehicleDynamics(baseConfig, {
        maximumSpeed: 90.4,
        boostedMaximumSpeed: 101.5,
        engineAcceleration: 18.2,
        brakeDeceleration: 30.5
      })
    },
    {
      id: "rival-3",
      name: "Solenne",
      laneBias: -0.52,
      linePhase: 0.47,
      aggression: 1.025,
      passSide: 1,
      dynamics: createVehicleDynamics(baseConfig, {
        maximumSpeed: 91.6,
        boostedMaximumSpeed: 103,
        engineAcceleration: 18.6,
        steeringResponse: 4.95
      })
    },
    {
      id: "rival-4",
      name: "Cinder",
      laneBias: 0.58,
      linePhase: 0.68,
      aggression: 0.965,
      passSide: -1,
      dynamics: createVehicleDynamics(baseConfig, {
        maximumSpeed: 87.9,
        boostedMaximumSpeed: 98.5,
        engineAcceleration: 18.9,
        brakeDeceleration: 31.8
      })
    },
    {
      id: "rival-5",
      name: "Aster",
      laneBias: 0.04,
      linePhase: 0.86,
      aggression: 1.04,
      passSide: -1,
      dynamics: createVehicleDynamics(baseConfig, {
        maximumSpeed: 92.2,
        boostedMaximumSpeed: 104.5,
        engineAcceleration: 18.35,
        aerodynamicDrag: 0.0019
      })
    }
  ];
};

const getOvertakeOffset = (
  vehicle: VehicleState,
  profile: RivalProfile,
  traffic: readonly VehicleState[]
): number => {
  let nearestGap = Number.POSITIVE_INFINITY;

  for (const other of traffic) {
    if (other.id === vehicle.id) {
      continue;
    }

    const gap = signedWrappedDelta(vehicle.trackProgress, other.trackProgress);
    const isBlocking =
      gap > 0 &&
      gap < 0.018 &&
      Math.abs(other.lateralOffset - vehicle.lateralOffset) < 2.2;

    if (isBlocking && gap < nearestGap) {
      nearestGap = gap;
    }
  }

  if (!Number.isFinite(nearestGap)) {
    return 0;
  }

  const urgency = 1 - nearestGap / 0.018;
  return profile.passSide * (1.25 + urgency * 1.35);
};

export const createRivalInput = (
  vehicle: VehicleState,
  profile: RivalProfile,
  traffic: readonly VehicleState[]
): InputFrame => {
  const lookAhead = 0.012 + (vehicle.speed / profile.dynamics.maximumSpeed) * 0.01;
  const curvature = sampleTrackCurvature(vehicle.trackProgress + lookAhead);
  const battle = calculateCapeBattleProfile(vehicle, profile, traffic);
  const racingLine =
    sampleRacingLine(vehicle.trackProgress + lookAhead, profile.laneBias, profile.linePhase) +
    getOvertakeOffset(vehicle, profile, traffic) * (1 - battle.engagement);
  const targetLine = lerp(racingLine, battle.laneTarget, battle.engagement);
  const lateralError = targetLine - vehicle.lateralOffset;
  const steering = clamp(
    lateralError * 0.17 +
      curvature * 0.24 -
      vehicle.heading * 0.58 -
      vehicle.slipAngle * 0.32,
    -1,
    1
  );
  const cornerDemand = Math.abs(curvature);
  const cornerSpeedRatio = clamp(1 - cornerDemand * 0.39, 0.56, 1);
  const racingTargetSpeed = profile.dynamics.maximumSpeed * cornerSpeedRatio * profile.aggression;
  const player = traffic.find((other) => other.id === "player");
  const battleTargetSpeed = player === undefined
    ? racingTargetSpeed
    : clamp(
      player.speed + battle.paceCorrection,
      Math.max(14, player.speed - 10),
      Math.min(profile.dynamics.maximumSpeed * 1.01, player.speed + 3)
    );
  const targetSpeed = lerp(racingTargetSpeed, battleTargetSpeed, battle.engagement);
  const speedError = targetSpeed - vehicle.speed;
  const racingThrottle = clamp(0.58 + speedError * 0.075, 0, 1);
  const targetSpeedRatio = saturate(targetSpeed / profile.dynamics.maximumSpeed);
  const battleHoldingForce =
    profile.dynamics.rollingResistance +
    profile.dynamics.aerodynamicDrag * targetSpeed * targetSpeed +
    cornerDemand * targetSpeedRatio * targetSpeedRatio * 3.8;
  const battleThrottle = clamp(
    battleHoldingForce / profile.dynamics.engineAcceleration + speedError * 0.075,
    0,
    1
  );
  const drifting = cornerDemand > 0.43 && vehicle.speed > 34 && Math.abs(steering) > 0.12;
  const boosting =
    vehicle.driftCharge > 0.58 &&
    cornerDemand < 0.2 &&
    speedError > 2 &&
    !vehicle.boostLocked;

  return createNeutralInput({
    throttle: lerp(racingThrottle, battleThrottle, battle.engagement),
    brake: clamp(-speedError * 0.09, 0, 1),
    steer: steering,
    drift: drifting,
    boost: boosting
  });
};
